import {
  JobStatus,
  JobStatusOperationFilterInput,
  JobTypeOperationFilterInput,
  LegionJob,
  LegionJobFilterInput,
  Query,
  StringOperationFilterInput,
  UuidOperationFilterInput,
} from 'graphql-api'
import {FiltersType as JobsFiltersType, GridSessionType as JobsGridSessionType} from 'redux/jobs/state'
import {FiltersType as TotalsFiltersType, GridSessionType as TotalsGridSessionType} from 'redux/totals/state'
import {FilterOptionType, FilterTypeType, HomeStatsArgsType} from 'commonTypes'
import {normalizePagination, normalizeSorting} from './common'
import {isUuid, upperCaseUnderscoreDelimitedToWords} from './stringUtility'

export const retrieveGraphQLJobs = (data: Query): LegionJob[] => {
  let jobs: LegionJob[] = []
  if (data.jobs?.edges && Array.isArray(data.jobs?.edges)) {
    jobs = data?.jobs?.edges?.map(edge => edge.node)
  }
  return jobs
}

export const normalizeFilters = (filters: JobsFiltersType) => {
  const {
    searchQuery,
    jobType,
    jobStatus,
    databaseId,
  } = filters

  const andFilters: LegionJobFilterInput[] = []

  if (searchQuery.length) {
    const searchOrFilters: LegionJobFilterInput[] = []
    searchOrFilters.push({
      raveUrl: {contains: searchQuery} as StringOperationFilterInput,
    })
    if (isUuid(searchQuery)) {
      searchOrFilters.push({
        jobId: {eq: searchQuery} as StringOperationFilterInput,
      })

      searchOrFilters.push({
        databaseId: {eq: searchQuery} as StringOperationFilterInput,
      })

      searchOrFilters.push({
        requestId: {eq: searchQuery} as StringOperationFilterInput,
      })
    }

    andFilters.push({
      or: searchOrFilters,
    })
  }

  if (jobType.length) {
    andFilters.push({
      jobType: {in: jobType} as JobTypeOperationFilterInput,
    })
  }

  if (jobStatus.length) {
    andFilters.push({
      status: {in: jobStatus} as JobStatusOperationFilterInput,
    })
  }

  if (databaseId.length) {
    andFilters.push({
      databaseId: {in: databaseId} as UuidOperationFilterInput,
    })
  }

  const where: LegionJobFilterInput = andFilters.length
    ? {and: andFilters}
    : {}

  return where
}

export const normalizeTotalsFilters = (filters: TotalsFiltersType) => {
  const {
    jobStatus,
    databaseId,
  } = filters

  const where = {} as HomeStatsArgsType
  if (databaseId.length) {
    where.databaseIds = databaseId
  }
  if (jobStatus.length) {
    where.jobsStatuses = jobStatus
  }

  return where
}

export const normalizeForApi = (gridSession: JobsGridSessionType) => {
  const {
    pagination: {pageSize},
    cursor,
    paginationDirection,
    sorting: sorting,
    filters: filters,
  } = gridSession

  const paginationParam = normalizePagination(pageSize, cursor, paginationDirection)
  const sortingParam = normalizeSorting(sorting)
  const filtersParam = normalizeFilters(filters)

  return {
    pagination: paginationParam,
    sorting: sortingParam,
    filters: filtersParam,
  }
}

export const normalizeForTotalsApi = (gridSession: TotalsGridSessionType) => {
  const {
    filters: filters,
  } = gridSession

  const filtersParam = normalizeTotalsFilters(filters)

  return {
    filters: filtersParam,
  }
}

export const getSelectedFilterOptions = (selectedFilters: FilterTypeType[]): FilterOptionType[] => selectedFilters
  .map(selectedFilter => {
    return ({
      value: selectedFilter,
      label: upperCaseUnderscoreDelimitedToWords(selectedFilter),
    } as FilterOptionType)
  })

export const getSelectedRaveUrlFilterOptions = (
  selectedRaveUrlFilters: FilterTypeType[],
  allRaveUrlFilters: FilterOptionType[]
): FilterOptionType[] => allRaveUrlFilters
  .reduce((selectedRaveUrlFilterOptions, raveUrlFilterOption) => {
    if (selectedRaveUrlFilters.includes(raveUrlFilterOption.value)) {
      selectedRaveUrlFilterOptions.push(raveUrlFilterOption)
    }
    return selectedRaveUrlFilterOptions
  }, [] as FilterOptionType[])

export const isJobPermissibleForRescheduling = (jobStatus: JobStatus) =>
  jobStatus === JobStatus.FinalError || jobStatus === JobStatus.Failed
